import { useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../api/client";

import { useT } from "../i18n/LanguageProvider";
import { LineField } from "../components/LineField";
import { FormHeader, CenteredForm } from "../components/FormHeader";
import { C, font, space } from "../styles/tokens";
import { BrandButton } from "../components/brand";

export function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    setError(null);
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError("Please provide a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post("/auth/forgot-password", {
        email: cleanEmail,
      });
      setSent(true);
      setError(null);
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      if (!status || status === 0) {
        setError(
          "Network error. Please check your internet connection and try again.",
        );
      } else if (status === 429) {
        setError(
          err?.message ||
            "Too many password reset requests. Please wait a few minutes before trying again.",
        );
      } else if (status >= 500) {
        setError("Something went wrong on our end. Please try again later.");
      } else {
        setError(
          err?.message || "Failed to send reset link. Please try again.",
        );
      }
      setSent(false);
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setSent(false);
    setError(null);
  }

  return (
    <CenteredForm>
      {sent ? (
        <SentState email={email} onReset={handleReset} />
      ) : (
        <FormState
          email={email}
          setEmail={setEmail}
          submitting={submitting}
          error={error}
          onSubmit={handleSubmit}
          t={t}
        />
      )}
    </CenteredForm>
  );
}

function FormState({ email, setEmail, submitting, error, onSubmit, t }) {
  return (
    <>
      <FormHeader
        title="Reset your password"
        subtitle="Enter the email you signed up with and we'll send a reset link."
      />
      <form onSubmit={onSubmit}>
        <LineField
          label={t("common.email")}
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        {error && (
          <div role="alert" style={errorBoxStyle}>
            {error}
          </div>
        )}
        <div style={{ marginTop: space.lg }}>
          <BrandButton
            type="submit"
            variant="primary"
            fullWidth
            loading={submitting}
          >
            {submitting ? "Sending..." : "Send reset link"}
          </BrandButton>
        </div>
      </form>
      <div
        style={{
          marginTop: space.lg,
          textAlign: "center",
          fontSize: 13,
          color: C.textMuted,
        }}
      >
        <Link
          to="/login"
          style={{ color: C.primary, fontWeight: 600, textDecoration: "none" }}
        >
          &larr; Back to sign in
        </Link>
      </div>
    </>
  );
}

function SentState({ email, onReset }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: C.primaryTint,
          color: C.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px", color: C.navy }}>
        Check your inbox
      </h1>
      <p
        style={{
          fontSize: 14,
          color: C.textMuted,
          lineHeight: 1.5,
          margin: "0 0 20px",
        }}
      >
        If <strong>{email}</strong> is registered on TickAndPass, you'll receive
        a password reset link shortly. Check your spam folder if it doesn't
        arrive.
      </p>
      <div>
        <Link to="/login" style={{ textDecoration: "none" }}>
          <BrandButton variant="primary" fullWidth>
            Back to sign in
          </BrandButton>
        </Link>
      </div>
      {onReset && (
        <div style={{ marginTop: space.md, textAlign: "center" }}>
          <button
            type="button"
            onClick={onReset}
            style={{
              background: "none",
              border: 0,
              color: C.primary,
              fontSize: 13,
              cursor: "pointer",
              fontFamily: font,
              textDecoration: "underline",
            }}
          >
            Try another email address
          </button>
        </div>
      )}
    </div>
  );
}

const errorBoxStyle = {
  fontSize: 13,
  color: C.danger,
  background: C.dangerTint,
  padding: "10px 12px",
  borderRadius: 8,
  marginTop: 8,
};
